type AccountLike = {
  accountNumber: string;
  brokerage?: string;
  nickname?: string;
  accountType?: string;
  isClosed?: boolean;
};

function isFuturesFamilySymbol(symbol: string | null | undefined) {
  return typeof symbol === 'string' && (symbol.startsWith('/') || symbol.startsWith('./'));
}

export function mapAccountsFromBalances(
  balances:
    | Record<
        string,
        | {
            brokerage?: string | null;
            nickname?: string | null;
            account_type?: string | null;
            is_closed?: boolean | null;
            isClosed?: boolean | null;
          }
        | undefined
      >
    | null
    | undefined
): AccountLike[] {
  if (!balances) {
    return [];
  }

  return Object.keys(balances).map((accountNumber) => {
    const balance = balances[accountNumber];
    return {
      accountNumber,
      brokerage: balance?.brokerage ?? undefined,
      nickname: balance?.nickname ?? undefined,
      accountType: balance?.account_type ?? undefined,
      isClosed:
        typeof balance?.is_closed === 'boolean'
          ? balance.is_closed
          : balance?.isClosed ?? undefined,
    };
  });
}

export function getSelectedAccount(
  accountNumber: string | null | undefined,
  accounts: AccountLike[]
) {
  if (!accountNumber || accountNumber === 'BACKTEST' || accountNumber === 'none') {
    return null;
  }

  return accounts.find((account) => account.accountNumber === accountNumber) ?? null;
}

export function isClosedSelectedAccount(
  accountNumber: string | null | undefined,
  accounts: AccountLike[]
) {
  return getSelectedAccount(accountNumber, accounts)?.isClosed === true;
}

function getSingleResolvedLiveBrokerage(accounts: AccountLike[]) {
  const brokerages = accounts
    .map((account) =>
      normalizeAccountBrokerage({
        accountNumber: account.accountNumber,
        brokerage: account.brokerage,
      })
    )
    .filter((brokerage) => brokerage && brokerage !== 'BACKTEST');

  const distinct = Array.from(new Set(brokerages));
  return distinct.length === 1 ? distinct[0] : null;
}

export function inferBrokerageFromAccountNumber(
  accountNumber: string | null | undefined
) {
  if (!accountNumber || accountNumber === 'BACKTEST' || accountNumber === 'none') {
    return null;
  }

  if (/^5W/i.test(accountNumber)) {
    return 'TASTYTRADE';
  }

  if (/^6Y/i.test(accountNumber)) {
    return 'TRADIER';
  }

  return null;
}

export function normalizeAccountBrokerage({
  accountNumber,
  brokerage,
}: {
  accountNumber: string | null | undefined;
  brokerage: string | null | undefined;
}) {
  if (brokerage && brokerage !== 'none') {
    return brokerage;
  }

  return inferBrokerageFromAccountNumber(accountNumber);
}

export function getBrokerageForSelectedAccount(
  accountNumber: string | null | undefined,
  accounts: AccountLike[]
) {
  if (!accountNumber || accountNumber === 'BACKTEST' || accountNumber === 'none') {
    return null;
  }
  const selectedAccount = accounts.find(
    (account) => account.accountNumber === accountNumber
  );
  return (
    normalizeAccountBrokerage({
      accountNumber,
      brokerage: selectedAccount?.brokerage,
    }) ?? null
  );
}

export function resolveMarketDataBrokerage({
  accountNumber,
  accounts,
  currentBrokerage,
  dataSource,
  symbol,
}: {
  accountNumber: string | null | undefined;
  accounts: AccountLike[];
  currentBrokerage: string | null | undefined;
  dataSource: string | null | undefined;
  symbol?: string | null | undefined;
}) {
  if (dataSource !== 'LIVE') {
    return currentBrokerage ?? '';
  }

  if (isFuturesFamilySymbol(symbol)) {
    return 'TASTYTRADE';
  }

  const selectedBrokerage = getBrokerageForSelectedAccount(accountNumber, accounts);
  if (selectedBrokerage) {
    return selectedBrokerage;
  }

  if (currentBrokerage && currentBrokerage !== 'BACKTEST' && currentBrokerage !== 'none') {
    return currentBrokerage;
  }

  const singleLiveBrokerage = getSingleResolvedLiveBrokerage(accounts);
  if (singleLiveBrokerage) {
    return singleLiveBrokerage;
  }

  // When running on BACKTEST (paper trading) with LIVE data and multiple live brokerages,
  // getSingleResolvedLiveBrokerage returns null because it can't pick one unambiguously.
  // Fall back to a preferred live brokerage so chart requests aren't silently dropped.
  if (accountNumber === 'BACKTEST' || currentBrokerage === 'BACKTEST') {
    const liveBrokerages = accounts
      .map((account) =>
        normalizeAccountBrokerage({
          accountNumber: account.accountNumber,
          brokerage: account.brokerage,
        })
      )
      .filter((b): b is string => !!b && b !== 'BACKTEST');
    if (liveBrokerages.includes('TRADIER')) return 'TRADIER';
    if (liveBrokerages.includes('TASTYTRADE')) return 'TASTYTRADE';
    if (liveBrokerages.length > 0) return liveBrokerages[0];
  }

  return '';
}

export function resolveBrokerageForExecution({
  accountNumber,
  accounts,
  currentBrokerage,
}: {
  accountNumber: string | null | undefined;
  accounts: AccountLike[];
  currentBrokerage: string | null | undefined;
}) {
  if (accountNumber === 'BACKTEST') {
    return 'BACKTEST';
  }

  const selectedBrokerage = getBrokerageForSelectedAccount(accountNumber, accounts);
  if (selectedBrokerage) {
    return selectedBrokerage;
  }

  return currentBrokerage ?? '';
}
